import { ApolloError } from '@apollo/client';
import { useState, useEffect, useMemo } from 'react';
import { FetchAvailableCountriesQuery } from '@css-xp/graphtypes';
import fetchAvailableCountriesQuery from './queries/fetch-available-countries';
import { abgGraphqlClient as graphqlClient } from '@atlassian/temp-client-utils';

interface CachedAvailableCountries {
  hasCountriesCompleted: boolean;
  isLoadingCountries: boolean;
  fetchCountriesError: ApolloError | undefined;
  countries: FetchAvailableCountriesQuery['availableCountries'] | undefined;
}

const LOCAL_STORAGE_KEY = 'availableCountries';

export const useCountryService = (): CachedAvailableCountries => {
  const [response, setResponse] = useState<CachedAvailableCountries>({
    hasCountriesCompleted: false,
    isLoadingCountries: false,
    fetchCountriesError: undefined,
    countries: undefined,
  });

  const gqlClient = graphqlClient();
  const updateResponse = (updatedResponse: CachedAvailableCountries) => {
    window.sessionStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(updatedResponse));
    setResponse(updatedResponse);
  };

  const getAllCountries = async () => {
    try {
      const countryData = await gqlClient.query({
        query: fetchAvailableCountriesQuery,
      });

      updateResponse({
        hasCountriesCompleted: true,
        isLoadingCountries: false,
        fetchCountriesError: undefined,
        countries: countryData?.data?.availableCountries,
      });
    } catch (err: any) {
      const updatedResponse = {
        hasCountriesCompleted: true,
        isLoadingCountries: false,
        fetchCountriesError: err,
        countries: undefined,
      };
      updateResponse(updatedResponse);
    }
  };

  useEffect(() => {
    const cachedResponse = getCachedValue();
    if (!cachedResponse || !cachedResponse.countries) {
      const cachedAvailableCountries = {
        hasCountriesCompleted: false,
        isLoadingCountries: true,
        fetchCountriesError: undefined,
        countries: undefined,
      };
      updateResponse(cachedAvailableCountries);
      getAllCountries();
    } else {
      setResponse(cachedResponse);
    }
  }, []);

  const getCachedValue = () => {
    const cachedValue = window.sessionStorage.getItem(LOCAL_STORAGE_KEY);

    return cachedValue ? (JSON.parse(cachedValue) as CachedAvailableCountries) : undefined;
  };

  return useMemo(() => {
    return response;
  }, [response]);
};

export default useCountryService;
